import { AgentProfile } from 'app/interfaces/profiles/Agent';
import { Profile, ProfileObject } from 'app/interfaces/UserProfile';
import { SavedTransactionFilter } from 'app/config/environment/partnerConfig';
import { ROLES } from 'app/configs/roles';
import { getPartnerData } from './partnerData';

/**
 * Get the agent profile from the local storage
 * @returns The agent profile stored in the local storage or null if not found
 */
export const getAgentProfile = (): AgentProfile | null => {
  const profile = localStorage.getItem(ROLES.AGENT);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

/**
 * Get the compliance profile from the local storage
 * @returns The compliance profile stored in the local storage or null if not found
 */
export const getComplianceProfile = (): Profile | null => {
  const profile = localStorage.getItem(ROLES.COMPLIANCE);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getManagingBrokerProfile = (): Profile | null => {
  const profile = localStorage.getItem(ROLES.MANAGING_BROKER);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getOfficeAdminProfile = (): Profile | null => {
  const profile = localStorage.getItem(ROLES.OFFICE_ADMIN);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const isOfficeAdmin = (): boolean => {
  const isOfficeAdmin =
    localStorage.getItem(ROLES.OFFICE_ADMIN) &&
    !localStorage.getItem(ROLES.ADMIN) &&
    !localStorage.getItem(ROLES.COMPLIANCE_ADMIN);
  return isOfficeAdmin ? true : false;
};

export const getOfficeAdminOffice = (): Record<string, any> | null => {
  const officeAdminProfile = getOfficeAdminProfile();
  return officeAdminProfile ? officeAdminProfile?.office || null : null;
};

export const getOfficeAdminOfficeName = (): string | null => {
  const officeAdminProfile = getOfficeAdminOffice();
  return officeAdminProfile?.name || null;
};

/**
 * Set the user profile in the local storage
 * @param profile The user profile to be stored
 * @returns void
 */
export const setProfiles = (profileObjects: ProfileObject[]) => {
  localStorage.setItem('profiles', JSON.stringify(profileObjects));
  for (let profileObject of profileObjects) {
    const role = profileObject.role;
    const profile = profileObject.profile;
    const email = profile.email;
    localStorage.setItem('email', JSON.stringify(email));
    localStorage.setItem(role, JSON.stringify(profile));
  }
};

const SAVED_FILTERS_KEY = 'savedTransactionFilters';

export const setSavedTransactionFilters = (
  filters: SavedTransactionFilter[] = []
) => {
  localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(filters));
};

export const getSavedTransactionFilters = (): SavedTransactionFilter[] => {
  try {
    const raw = localStorage.getItem(SAVED_FILTERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Get the name of the user from the user profile stored in the local storage
 * @returns The name of the user or null if not found
 */
export const getAgentName = (): string | null => {
  const profile = getAgentProfile();
  return profile ? profile.name : null;
};

/**
 * Get the partner company name of the partner the user is a part of
 * @returns The partner company name or null if not found
 */
export const getPartnerCompanyName = (): string | null | undefined => {
  const partnerData = getPartnerData();
  return partnerData ? partnerData.companyName : null;
};

export const setUser = (id: string) => {
  localStorage.setItem('userId', id);
};

export const getUserId = () => {
  const userId = localStorage.getItem('userId');
  return userId && userId !== undefined ? userId : null;
};

export const setDataConsentScreenShown = (dataTrackingScreenShown: boolean) => {
  localStorage.setItem(
    'dataTrackingScreenShown',
    JSON.stringify(dataTrackingScreenShown)
  );
};

export const getDataConsentScreenShown = () => {
  const dataTrackingScreenShown = localStorage.getItem(
    'dataTrackingScreenShown'
  );
  return dataTrackingScreenShown ? JSON.parse(dataTrackingScreenShown) : false;
};

export const getAgentAdminProfile = () => {
  const profile = localStorage.getItem(ROLES.AGENT_ADMIN);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getAgentOfficeAdminProfile = () => {
  const profile = localStorage.getItem(ROLES.AGENT_OFFICE_ADMIN);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getComplianceAdminProfile = () => {
  const profile = localStorage.getItem(ROLES.COMPLIANCE_ADMIN);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getAdminProfile = () => {
  const profile = localStorage.getItem(ROLES.ADMIN);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getClosingMemberProfile = () => {
  const profile = localStorage.getItem(ROLES.CLOSING_MEMBER);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getViewerProfile = (): Profile | null => {
  const profile = localStorage.getItem(ROLES.VIEWER);
  return profile && profile !== undefined ? JSON.parse(profile) : null;
};

export const getUserEmail = () => {
  const email = localStorage.getItem('email');
  return email && email !== undefined ? JSON.parse(email) : null;
};

export const getUserProfiles = () => {
  const profiles = localStorage.getItem('profiles');
  return profiles && profiles !== undefined && profiles !== '[]'
    ? JSON.parse(profiles)
    : [];
};

export const getLoggedInUserName = (): string | null => {
  const profiles = getUserProfiles();
  const firstProfileName = Array.isArray(profiles)
    ? profiles.find((p: ProfileObject) => p?.profile?.name)?.profile?.name
    : null;
  return (
    getComplianceProfile()?.name ||
    getAgentProfile()?.name ||
    firstProfileName ||
    null
  );
};

export const getUserRoles = (): any[] => {
  const profiles = getUserProfiles();
  if (Array.isArray(profiles) && profiles.length > 0) {
    return profiles.map((profile: ProfileObject) => profile?.role);
  }
  return [];
};

export const getUserPartnerID = (): string | null => {
  const partnerData = getPartnerData();
  return partnerData && partnerData._id ? partnerData._id : null;
};

/**
 * Set the hasOfficeWorkAllocation flag in local storage
 * @param hasOfficeWorkAllocation Boolean flag indicating if user has office work allocation
 */
export const setHasOfficeWorkAllocation = (
  hasOfficeWorkAllocation: boolean
) => {
  localStorage.setItem(
    'hasOfficeWorkAllocation',
    JSON.stringify(hasOfficeWorkAllocation)
  );
};

/**
 * Get the hasOfficeWorkAllocation flag from local storage
 * @returns Boolean flag or false if not found
 */
export const getHasOfficeWorkAllocation = (): boolean => {
  const hasOfficeWorkAllocation = localStorage.getItem(
    'hasOfficeWorkAllocation'
  );
  return hasOfficeWorkAllocation ? JSON.parse(hasOfficeWorkAllocation) : false;
};
